import React, { useEffect, useState, useCallback } from 'react';
import DateInput from '../../components/DateInput';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

const fmtDate = d => d ? format(new Date(d), 'dd/MM/yyyy') : '—';

const statusBadge = s => {
  const map = { Open:'badge-blue', Pending:'badge-orange', Closed:'badge-green', Cancelled:'badge-red',
    // legacy values (kept so old data still renders)
    Active:'badge-blue', Received:'badge-green' };
  return <span className={`badge ${map[s]||'badge-gray'}`}>{s}</span>;
};

export default function OrderList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ order_no:'', shipper:'', quality:'', contact_person:'', status: searchParams.get('status')||'', stage: searchParams.get('stage')||'', date_from:'', date_to:'' });
  const [total, setTotal] = useState(0);
  const [cancelTarget, setCancelTarget] = useState(null); // order being cancelled
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([,v])=>v));
      const res = await api.get('/orders', { params });
      setOrders(res.data.data);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleExport = async (type) => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([,v])=>v));
      const res = await api.get(`/orders/export/${type}`, { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: type === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders_${new Date().toISOString().slice(0,10)}.${type === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`Failed to export ${type.toUpperCase()}`);
    }
  };

  const openCancel = (order) => { setCancelTarget(order); setCancelReason(''); };
  const closeCancel = () => { if (!cancelBusy) { setCancelTarget(null); setCancelReason(''); } };
  const submitCancel = async () => {
    if (!cancelReason.trim()) { toast.error('Reason is required'); return; }
    setCancelBusy(true);
    try {
      await api.patch(`/orders/${cancelTarget.id}/cancel`, { reason: cancelReason.trim() });
      toast.success(`Order ${cancelTarget.order_no} cancelled`);
      setCancelTarget(null); setCancelReason('');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setCancelBusy(false);
    }
  };

  const handlePrintOrder = async (order) => {
    try {
      const res = await api.get(`/orders/${order.id}/print`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Failed to print order');
    }
  };

  const stepLabel = n => ['','Order Placed','Indent','BL Entry','Tracking','Payment','Doc Courier','Reconciliation','Rail Out','Transport','Receipt','Complaint','Voucher'][n] || `Step ${n}`;

  const stageLabels = {
    awaiting_bl: 'Awaiting BL',
    under_sea: 'Under Sea / Transit',
    at_port: 'At Port / Customs',
    pending_docs: 'Docs Pending Courier',
    payments_today: 'Payments Due Today',
    overdue_payments: 'Overdue Payments',
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Import Orders</h2>
          <p>
            {total} orders found
            {filters.stage && stageLabels[filters.stage] && (
              <span style={{marginLeft:8}}>
                · Filter: <span className="badge badge-blue">{stageLabels[filters.stage]}</span>
                <button className="btn btn-link btn-xs" style={{marginLeft:6}} onClick={()=>setFilters({...filters,stage:''})}>clear</button>
              </span>
            )}
          </p>
        </div>
        <div style={{display:'flex', gap:8}}>
          <button className="btn btn-outline" onClick={() => handleExport('excel')} disabled={loading || orders.length === 0}>Export Excel</button>
          <button className="btn btn-outline" onClick={() => handleExport('pdf')} disabled={loading || orders.length === 0}>Export PDF</button>
          <button className="btn btn-primary" onClick={() => navigate('/orders/new')}>+ New Order</button>
        </div>
      </div>

      <div className="filter-bar">
        {[['order_no','Order No'],['shipper','Shipper'],['quality','Quality'],['contact_person','Contact Person']].map(([k,lbl]) => (
          <div className="form-group" key={k}>
            <label>{lbl}</label>
            <input className="form-control" value={filters[k]} onChange={e=>setFilters({...filters,[k]:e.target.value})} placeholder={`Search ${lbl}`} />
          </div>
        ))}
        <div className="form-group">
          <label>Status</label>
          <select className="form-control" value={filters.status} onChange={e=>setFilters({...filters,status:e.target.value})}>
            <option value="">All</option>
            {['Open','Pending','Closed','Cancelled'].map(s=><option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>From</label>
          <DateInput className="form-control" value={filters.date_from} onChange={e=>setFilters({...filters,date_from:e.target.value})} />
        </div>
        <div className="form-group">
          <label>To</label>
          <DateInput className="form-control" value={filters.date_to} onChange={e=>setFilters({...filters,date_to:e.target.value})} />
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setFilters({ order_no:'',shipper:'',quality:'',contact_person:'',status:'',stage:'',date_from:'',date_to:'' })}>Clear</button>
      </div>

      <div className="card" style={{padding:0}}>
        {loading ? <div className="loading">Loading...</div> :
          orders.length === 0 ? <div className="empty-state">No orders found. <span style={{color:'var(--primary)',cursor:'pointer'}} onClick={()=>navigate('/orders/new')}>Create one?</span></div> :
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Order No</th><th>Date</th><th>Shipper</th><th>Contact Person</th><th>Quality</th><th>Origin</th>
                  <th>Contract Wt</th><th>Rate (USD)</th><th>BLs</th><th>Step</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} style={{cursor:'pointer'}} onClick={() => navigate(`/orders/${o.id}`)}>
                    <td className="fw-bold text-primary">{o.order_no}</td>
                    <td>{fmtDate(o.date_of_order)}</td>
                    <td>{o.shipper_name}</td>
                    <td>{o.contact_person || '—'}</td>
                    <td>{o.quality}</td>
                    <td>{o.origin_country}</td>
                    <td>{o.contract_weight_mt ? `${o.contract_weight_mt} MT` : '—'}</td>
                    <td>{o.rate_usd ? `$${o.rate_usd}` : '—'}</td>
                    <td style={{textAlign:'center'}}>{o.bl_count || 0}</td>
                    <td><span className="badge badge-blue" style={{fontSize:'10px'}}>{o.sub_status || stepLabel(o.workflow_step)}</span></td>
                    <td>{statusBadge(o.display_status || o.overall_status)}</td>
                    <td onClick={e=>e.stopPropagation()}>
                      <div style={{display:'flex', gap:4}}>
                        <button className="btn btn-outline btn-xs" onClick={()=>navigate(`/orders/${o.id}/edit`)}>Edit</button>
                        <button className="btn btn-outline btn-xs" onClick={()=>handlePrintOrder(o)} title="Print order with details">Print</button>
                        {o.overall_status !== 'Cancelled' && (
                          <button className="btn btn-outline btn-xs" style={{color:'var(--accent)',borderColor:'var(--accent)'}} onClick={()=>openCancel(o)} title="Cancel this order">Cancel</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </div>

      {cancelTarget && (
        <div onClick={closeCancel} style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'12px',padding:'24px',width:'min(480px,92vw)',boxShadow:'0 18px 48px rgba(15,23,42,0.25)'}}>
            <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'6px'}}>
              <div style={{width:'34px',height:'34px',borderRadius:'50%',background:'#fee2e2',color:'#b91c1c',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'18px',fontWeight:700}}>!</div>
              <h3 style={{margin:0,fontSize:'17px',fontWeight:700}}>Cancel Order {cancelTarget.order_no}?</h3>
            </div>
            <p style={{color:'var(--text-muted)',fontSize:'13px',marginTop:0,marginBottom:'14px'}}>This will mark the order as <b>Cancelled</b>. Please share a brief reason — it will appear in the printed PDF.</p>
            <label style={{fontSize:'12px',fontWeight:600,color:'var(--text-muted)'}}>Cancel Reason <span style={{color:'var(--accent)'}}>*</span></label>
            <textarea
              autoFocus
              className="form-control"
              rows={3}
              placeholder="e.g. Shipper backed out, weight mismatch, etc."
              value={cancelReason}
              onChange={e=>setCancelReason(e.target.value)}
              onKeyDown={e=>{ if (e.key==='Enter' && (e.ctrlKey||e.metaKey)) submitCancel(); if (e.key==='Escape') closeCancel(); }}
              style={{marginTop:'4px',resize:'vertical'}}
            />
            <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',marginTop:'18px'}}>
              <button className="btn btn-secondary btn-sm" onClick={closeCancel} disabled={cancelBusy}>Keep Order</button>
              <button className="btn btn-sm" style={{background:'var(--accent,#dc2626)',color:'#fff',borderColor:'transparent'}} onClick={submitCancel} disabled={cancelBusy || !cancelReason.trim()}>{cancelBusy?'Cancelling…':'Confirm Cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
